/**
 * Check Configuration Tool Page
 *
 * Verify device configuration profiles against expected values.
 * Tool color: purple
 *
 * Workflow:
 * 1. Select technology and configuration profile
 * 2. Manage serial ports and detect devices
 * 3. Check configuration and view comparison results
 */

import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  CheckCheck,
  CheckCircle,
  Loader2,
  Plus,
  Settings,
  XCircle,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router';
import { configurationProfileApi } from '@/api/production/configuration-profiles';
import { type ITechnology, technologyApi } from '@/api/production/technologies';
import { ConsoleDisplay } from '@/components/production/ConsoleDisplay';
import { TechnologySearchForm } from '@/components/production/TechnologySearchForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCheckConfiguration } from '@/hooks/production/useCheckConfiguration';
import { cn } from '@/lib/utils';
import { diffObjects, filterIrrelevantDiffs } from '@/lib/utils/diff';
import type { IConfigurationProfile } from '@/types/production/configuration-profile';

// ============================================
// Types
// ============================================

// ============================================
// Status Badge Component
// ============================================

function DeviceStatusBadge({
  status,
}: {
  status: 'fully_identified' | 'partially_identified' | 'not_identified';
}) {
  const config = {
    fully_identified: {
      label: 'Identificado',
      className: 'bg-green-100 text-green-800',
      dotClass: 'bg-green-500',
    },
    partially_identified: {
      label: 'Parcialmente Identificado',
      className: 'bg-yellow-100 text-yellow-800',
      dotClass: 'bg-yellow-500',
    },
    not_identified: {
      label: 'Não Identificado',
      className: 'bg-red-100 text-red-800',
      dotClass: 'bg-red-500',
    },
  };

  const { label, className, dotClass } = config[status];

  return (
    <div className="flex items-center gap-2">
      <div className={cn('h-2 w-2 rounded-full', dotClass)} />
      <span className={cn('text-xs font-medium', className)}>{label}</span>
    </div>
  );
}

// ============================================
// Detected Devices Table
// ============================================

function DetectedDevicesTable({
  devices,
}: {
  devices: Array<{ port: any; equipment?: any; status: any }>;
}) {
  if (devices.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Nenhum dispositivo detectado</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Serial</TableHead>
            <TableHead>IMEI</TableHead>
            <TableHead>ICCID</TableHead>
            <TableHead>Firmware</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device, index) => (
            <TableRow key={device.equipment?.serial || index}>
              <TableCell>
                <DeviceStatusBadge status={device.status} />
              </TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.serial ?? '--'}
              </TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.imei ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.iccid ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.firmware ?? '--'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Checked Devices Table
// ============================================

interface CheckedDevicesTableProps {
  checked: Array<{
    checked: boolean;
    checked_at: Date;
    equipment: any;
    config?: any;
    messages?: any[];
  }>;
  desiredProfile?: IConfigurationProfile | null;
}

// Helper function to get expected value from configuration profile
function getExpectedValueFromProfile(
  key: string,
  profile: IConfigurationProfile | null | undefined,
): string | undefined {
  if (!profile?.config) return undefined;

  // Remove 'r_' prefix from key (used in device commands but not in config keys)
  const normalizedKey = key.startsWith('r_') ? key.slice(2) : key;

  // Helper to get nested value using dot notation
  function getNestedValue(obj: Record<string, unknown> | undefined, path: string): unknown {
    if (!obj) return undefined;
    const keys = path.split('.');
    let current: unknown = obj;
    for (const k of keys) {
      if (current && typeof current === 'object' && k in (current as Record<string, unknown>)) {
        current = (current as Record<string, unknown>)[k];
      } else {
        return undefined;
      }
    }
    return current;
  }

  // Try direct key in general config
  let value = (profile.config.general as Record<string, unknown> | undefined)?.[normalizedKey];
  if (value !== undefined) {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  // Try nested key (e.g., "ip_primary.ip") in general config
  value = getNestedValue(
    profile.config.general as Record<string, unknown> | undefined,
    normalizedKey,
  );
  if (value !== undefined) {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  // Try direct key in specific config
  value = (profile.config.specific as Record<string, unknown> | undefined)?.[normalizedKey];
  if (value !== undefined) {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  // Try nested key in specific config
  value = getNestedValue(
    profile.config.specific as Record<string, unknown> | undefined,
    normalizedKey,
  );
  if (value !== undefined) {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  return undefined;
}

function CheckedDevicesTable({ checked, desiredProfile }: CheckedDevicesTableProps) {
  const [expandedItem, setExpandedItem] = React.useState<string | null>(null);

  if (checked.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          Nenhuma checagem realizada
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Complete as etapas anteriores para visualizar os resultados.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {checked.map((item, index) => {
        const serial = item.equipment.serial;
        const uniqueKey = `${serial}-${index}`;
        const isExpanded = expandedItem === uniqueKey;

        // Calcular diferenças se houver perfil desejado
        const rawDifferences =
          desiredProfile?.config && item.config
            ? [
                ...diffObjects({
                  desired: desiredProfile.config.general ?? {},
                  applied: (item.config as any).general ?? {},
                  keySelection: 'desired',
                }),
                ...diffObjects({
                  desired: desiredProfile.config.specific ?? {},
                  applied: (item.config as any).specific ?? {},
                  keySelection: 'desired',
                }),
              ]
            : [];

        // Ignora ruído de objeto vazio e campos write-only do BWS 4G com leitura vazia.
        // Mesma regra usada em useCheckConfiguration para que a tabela e o status "checked"
        // fiquem coerentes (sem mostrar divergência em campo que não conta como erro).
        let differences = filterIrrelevantDiffs(rawDifferences);

        // For NB2, filter out IP port differences when in DNS mode
        const isNb2 = desiredProfile?.technology_system_name === 'DM_BWS_NB2';
        if (isNb2) {
          differences = differences.filter((diff) => {
            // Ignore ip_primary.port and ip_secondary.port differences when in DNS mode
            if (diff.key === 'ip_primary.port' || diff.key === 'ip_secondary.port') {
              const ipKey = diff.key.split('.')[0];
              const ip = (desiredProfile?.config.general as any)?.[ipKey];
              if (ip && ip.ip === '0.0.0.0') {
                return false; // Ignore port difference in DNS mode
              }
            }
            return true;
          });
        }

        return (
          <div key={uniqueKey} className="rounded-lg border">
            {/* Header - Clickable */}
            <button
              type="button"
              className="w-full p-4 cursor-pointer hover:bg-muted/50 transition-colors text-left bg-transparent border-0 m-0"
              onClick={() => setExpandedItem(isExpanded ? null : uniqueKey)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {item.checked ? (
                    <Badge variant="success-subtle" className="gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Configuração OK
                    </Badge>
                  ) : (
                    <Badge variant="destructive-subtle" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      Diferenças Encontradas
                    </Badge>
                  )}
                  <span className="font-mono text-sm">{serial}</span>
                  {item.messages && item.messages.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {item.messages.length} comando(s)
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {new Date(item.checked_at).toLocaleString('pt-BR')}
                  </span>
                  <span
                    className={cn(
                      'transition-transform duration-200',
                      isExpanded ? 'rotate-180' : '',
                    )}
                  >
                    ▼
                  </span>
                </div>
              </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t p-4 space-y-4">
                {/* Commands Table */}
                {item.messages && item.messages.length > 0 && (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[15%]">Parâmetro</TableHead>
                          <TableHead className="w-[25%]">Esperado (Perfil)</TableHead>
                          <TableHead className="w-[25%]">Comando Enviado</TableHead>
                          <TableHead className="w-[35%]">Resposta Recebida</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {item.messages.map((msg: any, index: number) => {
                          const expectedValue = getExpectedValueFromProfile(
                            msg.key,
                            desiredProfile,
                          );
                          const displayKey = msg.key.startsWith('r_') ? msg.key.slice(2) : msg.key;
                          return (
                            <TableRow key={`${msg.key}-${index}`}>
                              <TableCell className="font-medium">{displayKey}</TableCell>
                              <TableCell className="font-mono text-sm">
                                {expectedValue !== undefined ? (
                                  <span className="text-purple-600">{expectedValue}</span>
                                ) : (
                                  <span
                                    className="text-yellow-600 bg-yellow-50 px-2 py-1 rounded text-xs flex items-center gap-1 w-fit"
                                    title="Valor não configurado no perfil"
                                  >
                                    <AlertTriangle className="h-3 w-3" />
                                    Não configurado
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="font-mono text-sm">
                                <span className="text-blue-600">{msg.request}</span>
                              </TableCell>
                              <TableCell className="font-mono text-sm">
                                {msg.response === undefined || msg.response === null ? (
                                  <span className="text-gray-400">–</span>
                                ) : typeof msg.response === 'object' ? (
                                  <span className="text-green-600">
                                    {JSON.stringify(msg.response)}
                                  </span>
                                ) : (
                                  <span className="text-green-600">{String(msg.response)}</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Differences Table (only if not checked) */}
                {!item.checked && differences.length > 0 && (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/3">Parâmetro</TableHead>
                          <TableHead className="w-1/3">Esperado</TableHead>
                          <TableHead className="w-1/3">Atual</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {differences.map((diff, index) => (
                          <TableRow key={`${diff.key}-${index}`}>
                            <TableCell className="font-medium">{diff.key}</TableCell>
                            <TableCell className="font-mono text-sm text-green-600">
                              {JSON.stringify(diff.desired)}
                            </TableCell>
                            <TableCell className="font-mono text-sm text-red-600">
                              {diff.applied === undefined || diff.applied === null ? (
                                <span className="text-gray-400">–</span>
                              ) : (
                                JSON.stringify(diff.applied)
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export default function CheckConfigurationPage() {
  const [searchParams] = useSearchParams();
  const technologyId = searchParams.get('technology_id');
  const profileId = searchParams.get('configuration_profile_id');
  const autoChecking = searchParams.get('auto_checking') === 'true';

  // State
  const [selectedTechnology, setSelectedTechnology] = React.useState<ITechnology | null>(null);
  const [selectedProfile, setSelectedProfile] = React.useState<IConfigurationProfile | null>(null);
  const [consoleLogs, setConsoleLogs] = React.useState<string[]>([]);

  // Fetch technology and profile if IDs are in URL
  const { data: technologyData } = useQuery({
    queryKey: ['technology', technologyId],
    queryFn: () => technologyApi.findOne(technologyId!),
    enabled: !!technologyId,
  });

  const { data: profileData } = useQuery({
    queryKey: ['configuration-profile', profileId],
    queryFn: () => configurationProfileApi.findOne(profileId!),
    enabled: !!profileId,
  });

  React.useEffect(() => {
    if (technologyData) setSelectedTechnology(technologyData);
  }, [technologyData]);

  React.useEffect(() => {
    if (profileData) setSelectedProfile(profileData);
  }, [profileData]);

  // Add log helper
  const addLog = React.useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    setConsoleLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
  }, []);

  const clearLogs = React.useCallback(() => {
    setConsoleLogs([]);
  }, []);

  // Handle technology change
  const handleTechnologyChange = React.useCallback(
    (tech: ITechnology | null) => {
      setSelectedTechnology(tech);
      setSelectedProfile(null);
      clearLogs();
      if (tech) addLog(`Tecnologia: ${tech.name.brand}`);
    },
    [addLog, clearLogs],
  );

  // Handle profile change
  const handleProfileChange = React.useCallback(
    (profile: IConfigurationProfile | null) => {
      setSelectedProfile(profile);
      if (profile) {
        const technologyName = profile.technology?.name || '';
        const profileName = profile.name;
        addLog(`Perfil de configuração selecionado: ${technologyName} ${profileName}`);
      }
    },
    [addLog],
  );

  // Use the check configuration hook
  const {
    checked,
    detected,
    check,
    requestPort,
    isChecking,
    isDetecting,
    autoCheckingEnabled,
    toggleAutoChecking,
  } = useCheckConfiguration({
    technology: selectedTechnology,
    configurationProfile: selectedProfile,
    configurationLog: null,
    autoChecking,
  });

  // Log when devices are detected
  const detectedSerialsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const currentSerials = new Set(
      detected.filter((d) => d.equipment?.serial).map((d) => d.equipment?.serial!),
    );
    const newSerials = Array.from(currentSerials).filter(
      (serial) => !detectedSerialsRef.current.has(serial),
    );

    newSerials.forEach((serial) => {
      addLog(`Dispositivo detectado: ${serial}`);
    });

    detectedSerialsRef.current = currentSerials;

    if (detected.length === 0 && detectedSerialsRef.current.size > 0) {
      addLog('Nenhum dispositivo detectado');
      detectedSerialsRef.current.clear();
    }
  }, [detected, addLog]);

  // Log when configuration check completes
  const lastCheckedLengthRef = React.useRef(0);
  React.useEffect(() => {
    const newCount = checked.length - lastCheckedLengthRef.current;
    if (newCount > 0) {
      const newChecks = checked.slice(-newCount);
      newChecks.forEach((checkResult) => {
        addLog(`Checagem: ${checkResult.equipment.serial}`);
        // Log commands sent and responses received
        checkResult.messages?.forEach((msg: any) => {
          addLog(`  → ${msg.key}: ${msg.request}`);
          if (msg.response) {
            const responseStr =
              typeof msg.response === 'object'
                ? JSON.stringify(msg.response)
                : String(msg.response);
            addLog(`  ← ${responseStr}`);
          }
        });
        if (checkResult.checked) {
          addLog(`✓ Configuração OK`);
        } else {
          addLog(`✗ Diferenças encontradas`);
        }
      });
    }
    lastCheckedLengthRef.current = checked.length;
  }, [checked, addLog]);

  // Check configuration with logging
  const handleCheck = React.useCallback(async () => {
    addLog('Iniciando checagem de configuração...');
    addLog(`Checando ${detected.length} dispositivo(s)`);
    await check(detected, selectedProfile, null);
  }, [check, detected, selectedProfile, addLog]);

  return (
    <div className="container mx-auto pb-8 px-4 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-base font-semibold leading-7 text-gray-900">
          Checagem de Configuração
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Verifique se os dispositivos estão configurados corretamente.
        </p>
      </div>

      {/* Step 1: Technology and Profile */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white">
              1
            </Badge>
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Tecnologia e Perfil</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            Escolha a tecnologia e perfil de configuração
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11 space-y-6">
          <TechnologySearchForm
            technology={selectedTechnology}
            configurationProfile={selectedProfile}
            showConfigurationProfile={true}
            onTechnologyChange={handleTechnologyChange}
            onConfigurationProfileChange={handleProfileChange}
          />

          <div className="flex items-center gap-2">
            <Switch checked={autoCheckingEnabled} onCheckedChange={toggleAutoChecking} />
            <Label>Ativar checagem automática</Label>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Ports */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white">
              2
            </Badge>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Gerenciamento de Portas</CardTitle>
            </div>
          </div>
        </CardHeader>
        {selectedTechnology ? (
          <CardContent className="ml-11 space-y-6">
            <DetectedDevicesTable devices={detected} />
            <Separator />
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <Button
                onClick={handleCheck}
                disabled={
                  !selectedProfile ||
                  detected.length === 0 ||
                  isChecking ||
                  (isDetecting && detected.length === 0) ||
                  autoCheckingEnabled
                }
                className="min-w-35 bg-black hover:bg-gray-800 text-white"
              >
                {isDetecting && detected.length === 0 ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Detectando
                  </>
                ) : isChecking ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Checando
                  </>
                ) : (
                  <>
                    <CheckCheck className="mr-2 h-4 w-4" />
                    Checar
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={requestPort}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Porta
              </Button>
            </div>
          </CardContent>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">
              Nenhuma tecnologia selecionada
            </h3>
          </div>
        )}
      </Card>

      {/* Step 3: Results */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center p-0 text-white',
                checked.length > 0 ? 'bg-black' : 'bg-gray-400',
              )}
            >
              3
            </Badge>
            <div className="flex items-center gap-2">
              <CheckCircle
                className={`h-5 w-5 ${checked.length > 0 ? 'text-black' : 'text-muted-foreground'}`}
              />
              <CardTitle className="text-xl">Resultados</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            {checked.length > 0
              ? `${checked.length} checagem(s) realizada(s)`
              : 'Aguardando checagem'}
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11 space-y-6">
          <CheckedDevicesTable checked={checked} desiredProfile={selectedProfile} />
          <ConsoleDisplay
            logs={consoleLogs}
            height={200}
            title="Log de Execução"
            textColor="green"
            bgColor="gray"
            onClear={clearLogs}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export { CheckConfigurationPage };
